import { confirmSignIn, signIn, signOut } from 'aws-amplify/auth';
import { useRef } from 'react';

import { useAxios } from '@/hooks/useAxios';

interface StepUpResult {
  token: string;
  expires_in: number;
}

export class MFAChallengeError extends Error {
  constructor() {
    super('MFA verification required');
    this.name = 'MFAChallengeError';
  }
}

export function useStepUpAuth() {
  const axios = useAxios({ doNotImpersonate: true });
  const nonceRef = useRef<string>('');

  const getStepUpToken = async (
    username: string,
    password: string,
    scope: string = 'recovery-codes'
  ): Promise<StepUpResult> => {
    // 1. Create step-up intent BEFORE signOut (requires current valid session)
    const intentResponse = await axios.post('/auth/step-up-intent', { scope });
    const { nonce } = intentResponse.data;
    nonceRef.current = nonce;

    // 2. Re-authenticate with Cognito directly (password never reaches our backend)
    // Guard: prevent tokenRefresh_failure from triggering logout during re-auth window
    // Save current auth tokens before clearing session so we can restore on failure
    const savedTokens: Record<string, string> = {};
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('CognitoIdentityServiceProvider.')) {
        const value = localStorage.getItem(key);
        if (value) savedTokens[key] = value;
      }
    });

    sessionStorage.setItem('step_up_reauth', 'true');
    try {
      await signOut({ global: false });
      const signInResult = await signIn({
        username,
        password,
        options: { clientMetadata: { step_up_nonce: nonce } },
      });

      // Handle MFA challenge if required
      if (
        !signInResult.isSignedIn &&
        signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
      ) {
        throw new MFAChallengeError();
      }

      // Request step-up token from backend (JWT now has custom:step_up_scope claim)
      const response = await axios.post('/auth/step-up', { scope });
      return response.data;
    } catch (error) {
      // Restore previous session tokens if re-auth failed (e.g., wrong password)
      // This prevents the user from being kicked out on failed attempts
      if (!(error instanceof MFAChallengeError)) {
        Object.entries(savedTokens).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });
      }
      throw error;
    } finally {
      sessionStorage.removeItem('step_up_reauth');
    }
  };

  const confirmMFAAndGetToken = async (
    code: string,
    scope: string = 'recovery-codes'
  ): Promise<StepUpResult> => {
    // Complete MFA challenge with nonce from getStepUpToken
    await confirmSignIn({
      challengeResponse: code,
      options: { clientMetadata: { step_up_nonce: nonceRef.current } },
    });

    // Now get step-up token with fresh session
    const response = await axios.post('/auth/step-up', { scope });
    return response.data;
  };

  return { getStepUpToken, confirmMFAAndGetToken };
}
